<script lang="ts" setup>
import {
  ref,
  useRouter,
  useRoute,
  useTemplateRef,
  callOnce,
  useMaps,
  computed,
  useHeaderStyle,
  clippedText,
  useCategories,
  useCreators,
  watch,
  useSmartQueryArray,
  usePlaces,
} from "#imports";
import { useSwipe, watchDebounced } from "@vueuse/core";
import MPCreatorTag from "~/components/widgets/MPCreatorTag.vue";
import MPLoader from "~/components/widgets/MPLoader.vue";
import MPTag from "~/components/widgets/MPTag.vue";
import MPSelect from "~/components/forms/MPSelect.vue";
import MPEmptyState from "~/components/layouts/MPEmptyState.vue";
import MPFilterToggle from "~/components/forms/MPFilterToggle.vue";

const mapStore = useMaps();
const categoryStore = useCategories();
const creatorStore = useCreators();
const placeStore = usePlaces();
const router = useRouter();
const route = useRoute();
useHeaderStyle("transparent");

const isLoading = ref<boolean>(true);
const currentIndex = ref<number>(0);
const categoryFilter = useSmartQueryArray("categories");
const creatorFilter = useSmartQueryArray("creators");
const placeFilter = useSmartQueryArray("place");

const data = computed(() =>
  mapStore.filteredMaps?.filter((m) => m.title && m.cover_image_url),
);

type FilterWatchArg = [
  typeof categoryFilter.value,
  typeof creatorFilter.value,
  typeof placeFilter.value,
];
watch(
  [categoryFilter, creatorFilter, placeFilter],
  async (newValue: FilterWatchArg, oldValue: FilterWatchArg) => {
    if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
      // nothing changed
      return;
    }
    isLoading.value = true;
    await mapStore.fetchFilteredMaps(
      {
        limit: 40,
        categories: categoryFilter.value,
        creators: creatorFilter.value,
        placeIds: placeFilter.value,
      },
      true,
    );

    currentIndex.value = 0;
    if (data.value?.[0]) {
      router.push({
        path: `/discover/maps/${data.value[0].id}`,
        query: route.query,
      });
    }
    isLoading.value = false;
  },
  { deep: true },
);

await callOnce(
  async () => {
    isLoading.value = true;
    await categoryStore.fetchCategories(null);
    await creatorStore.fetchCreatorsWithMaps(null);
    await placeStore.fetchTopPlaces({ limit: 50 });
    await mapStore.fetchFilteredMaps({ limit: 40 });

    isLoading.value = false;
    if (route.params.itemSlug) return;
    if (!data.value?.[0]) return;
    router.push(`/discover/maps/${data.value[0].id}`);
  },
  {
    mode: "navigation",
  },
);

const feedItem = useTemplateRef("feedItem");
const { isSwiping, direction } = useSwipe(feedItem);
const swipeDirection = ref<string>("up");
const slideTime = 400; // milliseconds of animation length
const isSliding = ref<boolean>(false);

watchDebounced(
  isSwiping,
  () => {
    if (!data.value || isSliding.value) return;
    if (direction.value === "up") {
      swipeDirection.value = "up";
      if (!data.value[currentIndex.value + 1]) return;
      currentIndex.value++;
      router.push(`/discover/maps/${data.value[currentIndex.value]!.id}`);
    }
    if (direction.value === "down") {
      swipeDirection.value = "down";
      if (!data.value[currentIndex.value - 1]) return;
      currentIndex.value--;
      router.push(`/discover/maps/${data.value[currentIndex.value]!.id}`);
    }
    isSliding.value = true;
    setTimeout(() => (isSliding.value = false), slideTime);
  },
  { debounce: 500, maxWait: 0 },
);

const resetFilters = () => {
  categoryFilter.value = [];
  creatorFilter.value = [];
  placeFilter.value = [];
};
</script>

<template>
  <div>
    <div class="page-view">
      <h1 class="typeface-title">Discover</h1>
      <p class="typeface-summary">
        Explore curated lists from local experts and global creators
      </p>
      <div class="filter-container">
        <div
          :class="['select-destinations', { active: placeFilter.length > 0 }]"
        >
          <MPSelect
            v-if="placeStore.topPlaces"
            v-model="placeFilter"
            name="destination"
            placeholder="Choose a Destination"
            :options="
              placeStore.topPlaces.map((place) => ({
                label: place.fullName,
                value: place.id,
              }))
            "
            isMultiple
            isSearchable
            isClearable
          />
        </div>
        <MPFilterToggle class="filter-toggle" @reset="resetFilters">
          <MPSelect
            v-if="placeStore.topPlaces"
            v-model="placeFilter"
            name="cities"
            placeholder="Filter by City"
            :options="
              placeStore.topPlaces.map((place) => ({
                label: place.fullName,
                value: place.id,
              }))
            "
            isSearchable
            isMultiple
            isClearable
          />
          <MPSelect
            v-if="categoryStore.categories"
            name="categories"
            v-model="categoryFilter"
            placeholder="Filter by Category"
            :options="
              categoryStore.categories!.map((cat) => ({
                label: cat.name,
                value: cat.name,
              }))
            "
            isSearchable
            isMultiple
            isClearable
          />
          <MPSelect
            v-if="creatorStore.creatorsWithMaps"
            v-model="creatorFilter"
            name="creators"
            placeholder="Filter by Creator"
            :options="
              creatorStore.creatorsWithMaps.map((creator) => ({
                label: `@${creator.mappr_handle}`,
                value: creator.mappr_handle!,
              }))
            "
            isSearchable
            isMultiple
            isClearable
          />
        </MPFilterToggle>
      </div>

      <div v-if="(data?.length ?? 0) === 0" class="emtpy-state">
        <MPEmptyState
          title="No Maps found"
          subtitle="We dont have any results for your filter"
          imageSrc="/images/empty-state-maps.png"
        />
      </div>
      <ul class="discover-list" v-else>
        <li
          v-for="item in data"
          :key="item.id"
          :style="{ backgroundImage: `url(${item.cover_image_url})` }"
        >
          <NuxtLink class="typeface-detail" :to="`/maps/${item.current_slug}`">
            <MPTag rounded size="medium" version="transparent">
              <i class="ri-map-line" />
              Map
            </MPTag>
            <h4>{{ item.title }}</h4>
            <p class="discover-card-summary">
              {{ clippedText(item.summary ?? "", 100) }}
            </p>
            <MPCreatorTag :creatorId="item.user_id" />
          </NuxtLink>
        </li>
      </ul>
    </div>
    <div class="slider-view">
      <div v-if="isLoading">
        <MPLoader label="Loading..." />
      </div>
      <div v-else ref="feedItem">
        <div class="filter-toggle-mobile">
          <div
            :class="['select-destinations', { active: placeFilter.length > 0 }]"
          >
            <MPSelect
              v-if="placeStore.topPlaces"
              v-model="placeFilter"
              name="destination"
              placeholder="Choose a Destination"
              :options="
                placeStore.topPlaces.map((place) => ({
                  label: place.fullName,
                  value: place.id,
                }))
              "
              isSearchable
              isMultiple
              isClearable
            />
          </div>
          <MPFilterToggle class="filter-toggle" @reset="resetFilters">
            <MPSelect
              v-if="placeStore.topPlaces"
              v-model="placeFilter"
              name="cities"
              placeholder="Filter by City"
              :options="
                placeStore.topPlaces.map((place) => ({
                  label: place.fullName,
                  value: place.id,
                }))
              "
              isSearchable
              isMultiple
              isClearable
            />
            <MPSelect
              v-if="categoryStore.categories"
              name="categories"
              v-model="categoryFilter"
              placeholder="Filter by Category"
              :options="
                categoryStore.categories!.map((cat) => ({
                  label: cat.name,
                  value: cat.name,
                }))
              "
              isSearchable
              isMultiple
              isClearable
            />
            <MPSelect
              v-if="creatorStore.creatorsWithMaps"
              v-model="creatorFilter"
              name="creators"
              placeholder="Filter by Creator"
              :options="
                creatorStore.creatorsWithMaps.map((creator) => ({
                  label: creator.mappr_handle!,
                  value: creator.mappr_handle!,
                }))
              "
              isSearchable
              isMultiple
              isClearable
            />
          </MPFilterToggle>
        </div>
        <Transition :name="swipeDirection === 'up' ? 'slide-up' : 'slide-down'">
          <NuxtPage />
        </Transition>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";

.header {
  position: absolute;
  top: 0;
  z-index: 1000;
  width: 100%;
  padding: 0.5rem 0.5rem 5rem 3.5rem;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.6) 40.02%,
    rgba(0, 0, 0, 0.29) 67.7%,
    rgba(0, 0, 0, 0) 100%
  );
}

.page-view {
  display: none;
  @mixin screen md {
    display: flex;
    max-width: 100rem;
    margin: 4rem auto 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    h1 {
      margin: 4rem 0 0;
    }
    p {
      color: var(--color-slate-400);
    }
  }
  .discover-list {
    padding: 3rem;
    list-style: none;
    margin: 0 0 2rem 0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    justify-content: center;
    gap: 1.25rem;
    li {
      width: auto;
      position: relative;
      border-radius: 0.5rem;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      min-height: 28rem;
      display: flex;
      flex-direction: column;
      justify-content: end;
      padding: 1rem 2.5rem 1.5rem 0.5rem;
      overflow: hidden;
      margin-bottom: 1rem;
      > * {
        z-index: 2;
      }
      span.location {
        margin: 0;
        color: var(--color-rose-400);
      }
      h4 {
        margin: 1rem 0 1.25rem;
        padding-left: 0.25rem;
        color: white;
      }
      p {
        display: block;
        margin: 0.5rem 0 2rem;
        line-height: 1.25 !important;
        padding-left: 0.25rem;
        color: var(--color-slate-50);
      }
      a {
        color: var(--color-slate-300);
        padding-left: 0.25rem;
      }
    }
    li:after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 18rem;
      z-index: 1;
      backdrop-filter: blur(1px);
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.6) 30%,
        rgba(0, 0, 0, 0.9) 50%
      );
    }
  }
  @mixin screen lg {
    .discover-list {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  @mixin screen xl {
    .discover-list {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  @mixin screen xxxl {
    .discover-list {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}

.filter-container {
  position: relative;
}

.filter-toggle {
  z-index: 3;
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background-color: white;
  border-radius: 2rem;
  @mixin screen md {
    right: 0;
  }

  &:before {
    content: "";
    margin: 0.25rem 0;
    border: 1px solid var(--color-slate-50);
  }
}

.select-destinations {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 auto;
  width: calc(100vw - 4rem);
  border-radius: 2rem;
  transition: width 0.2s ease-in-out;

  @mixin screen md {
    width: 21rem;
    margin: 0;
    border: 1px solid var(--color-slate-100);
  }

  :deep(label) {
    border-radius: 2rem;
    border: none;
    @mixin screen md {
      box-shadow: var(--shadow-sm);
    }
  }
  :deep(.select-trigger) {
    padding: 0.9rem 1rem !important;
    @mixin screen md {
      padding: 1rem 1rem !important;
      box-shadow: var(--shadow-sm);
    }
  }

  :deep(button) {
    @mixin screen md {
      min-width: 2.15rem !important;
      min-height: 2.15rem !important;
    }
  }
}

.select-destinations.active {
  @mixin screen-down md {
    width: calc(100vw - 2rem);
    transition: width 0.2s ease-in-out;
  }
}

.slider-view {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: grid;
  align-items: center;
  justify-content: center;
  height: 100dvh;
  overflow: hidden;
  background-color: black;
  @mixin screen md {
    display: none;
  }
}

.filter-toggle-mobile {
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  z-index: 10;

  .select-destinations {
    width: calc(100vw - 5rem);
  }
}

.emtpy-state {
  padding: 2rem 0;
}
</style>
